/**
 * The talent dashboard's building blocks: tiles, panels, bar lists and the
 * people table every view ends with. Same look as the Workforce Explorer's
 * panels, without its metric-definition plumbing.
 */
import React, { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, FileSpreadsheet } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ReportingPerson } from '@/services/api/talentReportingService'
import type { CountRow } from './talentDashboardData'

export const Tile: React.FC<{ label: string; value: ReactNode; sub?: ReactNode; tone?: 'default' | 'warn' }> = ({
  label,
  value,
  sub,
  tone = 'default',
}) => (
  <Card className={cn(tone === 'warn' && 'border-amber-200 bg-amber-50/60')}>
    <CardContent className="p-3">
      <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold leading-none tabular-nums text-heritage-blue">{value}</div>
      {sub ? <div className="mt-1 text-[11px] leading-snug tabular-nums text-gray-500">{sub}</div> : null}
    </CardContent>
  </Card>
)

export const TileRow: React.FC<{ children: ReactNode; min?: string }> = ({ children, min = '180px' }) => (
  <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}>
    {children}
  </div>
)

export const Panel: React.FC<{
  title: string
  note?: ReactNode
  right?: ReactNode
  className?: string
  children: ReactNode
}> = ({ title, note, right, className, children }) => (
  <Card className={cn('overflow-hidden', className)}>
    <CardContent className="p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold leading-tight text-heritage-blue">{title}</h2>
          {note ? <div className="mt-0.5 text-[11px] leading-snug text-gray-500">{note}</div> : null}
        </div>
        {right ? <div className="flex shrink-0 items-center gap-2">{right}</div> : null}
      </div>
      {children}
    </CardContent>
  </Card>
)

/** Horizontal bars, one per value, with the count and its share. */
export const BarList: React.FC<{ rows: CountRow[]; empty?: string; max?: number }> = ({
  rows,
  empty = 'Nobody to count with these filters.',
  max = 12,
}) => {
  if (!rows.length) return <p className="text-xs text-gray-500">{empty}</p>
  const top = Math.max(...rows.map((r) => r.count), 1)
  const shown = rows.slice(0, max)
  const rest = rows.slice(max)
  const restCount = rest.reduce((n, r) => n + r.count, 0)
  return (
    <ul className="space-y-1.5" role="list">
      {shown.map((r) => (
        <li key={r.label} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-2 text-xs">
          <span className="truncate text-gray-700" title={r.label}>{r.label}</span>
          <span className="h-3 rounded-sm bg-gray-100" aria-hidden="true">
            <span className="block h-3 rounded-sm bg-heritage-blue/70" style={{ width: `${(r.count / top) * 100}%` }} />
          </span>
          <span className="w-20 text-right tabular-nums text-gray-600">
            {r.count} <span className="text-gray-400">({r.share}%)</span>
          </span>
        </li>
      ))}
      {rest.length ? (
        <li className="text-[11px] text-gray-500">
          and {rest.length} more, {restCount} {restCount === 1 ? 'person' : 'people'}
        </li>
      ) : null}
    </ul>
  )
}

export interface PeopleColumn {
  key: string
  label: string
  render: (p: ReportingPerson) => ReactNode
  /** What the column sorts by; columns without one do not sort. */
  sortValue?: (p: ReportingPerson) => string | number
  className?: string
}

const PAGE = 50

/** The people behind a view, sortable, fifty at a time, with its own download. */
export function PeopleTable({
  title,
  note,
  people,
  columns,
  onExport,
  empty = 'Nobody with these filters.',
}: {
  title: string
  note?: ReactNode
  people: ReportingPerson[]
  columns: PeopleColumn[]
  onExport?: () => void
  empty?: string
}) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [limit, setLimit] = useState(PAGE)
  const sorted = useMemo(() => {
    const col = sort ? columns.find((c) => c.key === sort.key) : undefined
    if (!col?.sortValue) return people
    const value = col.sortValue
    return [...people].sort((a, b) => {
      const x = value(a)
      const y = value(b)
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort!.dir
      return String(x).localeCompare(String(y), 'en', { sensitivity: 'base', numeric: true }) * sort!.dir
    })
  }, [people, columns, sort])
  const toggle = (key: string) =>
    setSort((s) => (s && s.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }))

  return (
    <Panel
      title={title}
      note={note ?? `${people.length} ${people.length === 1 ? 'person' : 'people'}`}
      right={
        onExport ? (
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onExport} disabled={!people.length}>
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Download
          </Button>
        ) : undefined
      }
    >
      {people.length === 0 ? (
        <p className="text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wide text-gray-500">
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={cn('px-2 py-1.5 font-semibold', c.className)}>
                    {c.sortValue ? (
                      <button type="button" className="inline-flex items-center gap-1 hover:text-heritage-blue" onClick={() => toggle(c.key)}>
                        {c.label}
                        {sort?.key === c.key ? (
                          sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : null}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, limit).map((p) => (
                <tr key={p.employee_id} className="border-b border-gray-100 align-top">
                  {columns.map((c) => (
                    <td key={c.key} className={cn('px-2 py-1.5 text-gray-700', c.className)}>
                      {c.render(p)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length > limit ? (
            <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
              Showing {limit} of {sorted.length}.
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLimit((n) => n + PAGE)}>
                Show {Math.min(PAGE, sorted.length - limit)} more
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLimit(sorted.length)}>
                Show all
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}
